// ブラシプリセットとカスタム先端の保存 (localStorage)。
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { nanoid } from 'nanoid'
import type { BrushSettings } from '@/stores/uiStore'
import { useUiStore } from '@/stores/uiStore'
import { useDocumentStore } from '@/stores/documentStore'
import { readComposite } from '@/app/engine'
import { maskToAlpha } from '@/brush/fill'
import { tipFromRgba, encodeTipData, decodeTipData, type BrushTip } from './tips'

export interface BrushPreset {
  id: string
  name: string
  settings: BrushSettings
}

const KEY = 'bpe.brushes.v1'
interface Stored {
  tips: { id: string; name: string; w: number; h: number; b64: string }[]
  presets: BrushPreset[]
}

function load(): { tips: BrushTip[]; presets: BrushPreset[] } {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { tips: [], presets: [] }
    const v = JSON.parse(raw) as Partial<Stored>
    const tips: BrushTip[] = []
    for (const t of v.tips ?? []) {
      if (!t || typeof t.id !== 'string' || typeof t.w !== 'number' || typeof t.h !== 'number' || typeof t.b64 !== 'string') continue
      const data = decodeTipData(t.b64, t.w * t.h)
      if (data) tips.push({ id: t.id, name: String(t.name ?? 'tip'), w: t.w, h: t.h, data })
    }
    const presets = (v.presets ?? []).filter(p => p && typeof p.id === 'string' && typeof p.name === 'string' && p.settings && typeof p.settings === 'object')
    return { tips, presets }
  } catch {
    return { tips: [], presets: [] }
  }
}

export const useBrushStore = defineStore('brushes', () => {
  const init = load()
  const tips = ref<BrushTip[]>(init.tips)
  const presets = ref<BrushPreset[]>(init.presets)
  function save() {
    try {
      const st: Stored = { tips: tips.value.map(t => ({ id: t.id, name: t.name, w: t.w, h: t.h, b64: encodeTipData(t.data) })), presets: presets.value }
      localStorage.setItem(KEY, JSON.stringify(st))
    } catch {
      /* ignore */
    }
  }
  watch([tips, presets], save, { deep: true })

  function tipById(id: string | undefined): BrushTip | null {
    if (!id || id === 'round') return null
    return tips.value.find(t => t.id === id) ?? null
  }
  /** 選択範囲 (無ければ文書全体) の合成画像から先端を定義し、現在のブラシに設定する */
  function defineTipFromSelection(name?: string): BrushTip | null {
    const docStore = useDocumentStore()
    const doc = docStore.doc
    if (!doc) return null
    const c = readComposite()
    const mask = docStore.selectionMask()
    const rect = mask ? mask.rect : { x: 0, y: 0, w: c.width, h: c.height }
    const rgba = new Uint8ClampedArray(rect.w * rect.h * 4)
    for (let y = 0; y < rect.h; y++) rgba.set(c.rgba.subarray(((rect.y + y) * c.width + rect.x) * 4, ((rect.y + y) * c.width + rect.x + rect.w) * 4), y * rect.w * 4)
    const sel = mask ? maskToAlpha(mask, rect) : null
    const t = tipFromRgba(rgba, rect.w, rect.h, sel)
    const tip: BrushTip = { id: 'tip_' + nanoid(6), name: name ?? `ブラシ ${tips.value.length + 1}`, w: t.w, h: t.h, data: t.data }
    tips.value.push(tip)
    const ui = useUiStore()
    ui.brush.tipId = tip.id
    return tip
  }
  function removeTip(id: string) {
    tips.value = tips.value.filter(t => t.id !== id)
    const ui = useUiStore()
    if (ui.brush.tipId === id) ui.brush.tipId = 'round'
    if (ui.eraser.tipId === id) ui.eraser.tipId = 'round'
  }
  function savePreset(name: string, settings: BrushSettings): BrushPreset {
    const p: BrushPreset = { id: 'bp_' + nanoid(6), name, settings: { ...settings } }
    presets.value.push(p)
    return p
  }
  function applyPreset(id: string) {
    const p = presets.value.find(x => x.id === id)
    if (!p) return
    const ui = useUiStore()
    ui.brush = { ...ui.brush, ...p.settings }
  }
  function removePreset(id: string) {
    presets.value = presets.value.filter(p => p.id !== id)
  }
  return { tips, presets, tipById, defineTipFromSelection, removeTip, savePreset, applyPreset, removePreset }
})
